/**
 * usePatientImportSession Hook
 *
 * Manages session polling and status-driven flow for the import wizard.
 * Polls session status and stops when a user-action or terminal status is reached.
 *
 * Uses manual setInterval + refetch instead of RTK Query's pollingInterval
 * so that shouldPoll is derived directly from the query data (no state/effect
 * sync delay that can cause runaway polls).
 */

import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SentryService from '@services/SentryService';
import { useEventTracking } from '@services/statsig';

import {
  useDeleteImportSessionMutation,
  useGetImportSessionStatusQuery,
} from '../store/hooks';
import type { ImportSessionStatus } from '../types';
import { POLLING_STATUSES } from '../types';

const POLLING_INTERVAL_MS = 10_000;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000; // 10 minutes

export interface UsePatientImportSessionReturn {
  status: ImportSessionStatus | undefined;
  session: {
    sessionId: string;
    organizationId?: string;
    fileType?: string;
    totalPatients?: number;
    processedPatients?: number;
    failedPatients?: number;
    progressPercentage?: number;
    errorMessages?: string[];
  } | undefined;
  isPolling: boolean;
  cancelSession: () => Promise<void>;
  isCancelling: boolean;
}

export function usePatientImportSession(sessionId: string): UsePatientImportSessionReturn {
  const navigate = useNavigate();
  const { patientImport } = useEventTracking();

  const [deleteSession, { isLoading: isCancelling }] = useDeleteImportSessionMutation();

  // Fetch session status (no built-in polling — we handle it manually below)
  const { data, refetch } = useGetImportSessionStatusQuery(
    { sessionId },
    { refetchOnFocus: false },
  );

  const status = data?.status as ImportSessionStatus | undefined;

  // Derive shouldPoll directly from the current query data — no state indirection
  const shouldPoll = !status || POLLING_STATUSES.includes(status);

  // Manual polling via setInterval + refetch.
  // Stops immediately when shouldPoll becomes false (no render-delay issues).
  // Also caps total poll duration and skips polls when the tab is hidden.
  const pollStartRef = useRef(Date.now());
  useEffect(() => {
    if (!shouldPoll) return;

    const interval = setInterval(() => {
      // Stop after MAX_POLL_DURATION_MS
      if (Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
        clearInterval(interval);
        return;
      }
      // Skip polls when the tab is in the background
      if (document.hidden) return;

      refetch();
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [shouldPoll, refetch]);

  // Track terminal status transitions
  const prevStatusRef = useRef<ImportSessionStatus | undefined>(undefined);
  useEffect(() => {
    if (!status || status === prevStatusRef.current) return;
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;

    if (status === 'completed' && prev !== 'completed') {
      patientImport.completed({
        session_id: sessionId,
        imported_count: data?.processed_patients,
        failed_count: data?.failed_patients,
        new_patients: data?.processed_patients,
      });
    } else if (status === 'failed' && prev !== 'failed') {
      patientImport.failed({
        session_id: sessionId,
        error_count: data?.error_messages?.length,
      });
    }
  }, [status, sessionId, data, patientImport]);

  const isPolling = shouldPoll;

  const session = data
    ? {
        sessionId: data.session_id,
        organizationId: data.organization_id,
        fileType: data.file_type,
        totalPatients: data.total_patients,
        processedPatients: data.processed_patients,
        failedPatients: data.failed_patients,
        progressPercentage: data.progress_percentage,
        errorMessages: data.error_messages?.map((e) => e.message) ?? [],
      }
    : undefined;

  const cancelSession = useCallback(async () => {
    patientImport.cancelled({ session_id: sessionId });
    try {
      await deleteSession({ sessionId }).unwrap();
    } catch (err: unknown) {
      // Proceed even if delete fails
      SentryService.captureException(err, {
        level: 'warning',
        tags: { component: 'usePatientImportSession', feature: 'patient-import' },
        extra: { method: 'cancelSession', sessionId },
      });
    }
    navigate('/patients');
  }, [deleteSession, sessionId, navigate, patientImport]);

  return {
    status,
    session,
    isPolling,
    cancelSession,
    isCancelling,
  };
}
